/**
 * GuardianSection — guardian identity when the patient is a minor
 * (`isAdult === false`). Mirrors Flutter `guardians_identity_verification.dart`
 * + `identity_child_widget.dart` with `isGuardian: true`.
 *
 * Fields:
 *   - First / Middle / Last name (the guardian's, not the patient's)
 *   - Birth date
 *   - Gender (pills, reuses /api/Master/Gender)
 *   - ID type (dropdown, /api/Master/IdentityType)
 *   - ID number
 *   - Relationship — static text input here; Flutter has a `useRelations`
 *     typeahead with category 'guardian' which converts to a Command/
 *     Popover combobox in a small follow-up.
 *   - Email (optional)
 *   - Contact number (simple text; full country-dial picker as in
 *     Demographics is a follow-up extraction)
 *
 * Out of scope here:
 *   - Matching-records save-gate for the GUARDIAN identity is wired
 *     here (since PROD-777). When `firstName + lastName + birthDate +
 *     gender + idType + idNumber` settle, fires `useMatchingPatients`
 *     debounced 500 ms and reports the count via
 *     `onMatchingCountChange` so the tab body's `saveDisabled` can
 *     fold it in. Flutter parity at
 *     `guardians_identity_verification.dart:75-82`.
 */

import { useEffect, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { requiredTone } from './required-tone';
import { TextField, BrandSpinner } from '@scripsteam/scrips-signal-ds';
import { DatePickerField } from '@/shared/ui/components';
import { adultMaxDob } from './dob-bounds';
import { idExpiryMin } from './id-expiry-bounds';
import { PillPicker } from './form-primitives';
import { useMissingFields } from './missing-fields-context';
import { FieldLabel } from '@scripsteam/scrips-signal-ds';
import { useGenders, useIdTypes, useRelations } from '@/shared/services/lookups';
import { useMatchingPatients } from '@/patients/services/use-matching-patients';
import { useDebounce } from '@/shared/hooks/use-debounce';
import { MasterTypeahead } from './master-typeahead';
import { formatEmiratesId } from './emirates-id-format';
import { GuardianMatchTable } from './guardian-match-table';
import { useSuppressibleCount } from './use-suppressible-count';
import { isEmiratesIdType } from '@/patients/lib/identity-constants';
import type { PersonalDetailsFormValues } from './personal-details-schema';
import type { MatchingPatient } from '@/types/patient-api';

/**
 * Find an EXACT ID-number match to auto-link as the guardian, so siblings share
 * ONE guardian record instead of creating duplicates. Only matches with a real
 * `patientId` qualify — linking stamps the guardian's `id` from it, which is
 * what makes the caller's effect self-terminate (a match with a null patientId
 * couldn't set `id` and would re-fire every render). Name-only / fuzzy matches
 * are deliberately NOT auto-linked — those stay for optional manual selection.
 * Exported for unit testing.
 */
export function findExactGuardianIdMatch(
  matches: MatchingPatient[],
  enteredIdNumber: string,
): MatchingPatient | undefined {
  // Normalise before comparing: the guardian Emirates-ID input stores digits
  // only (dashes stripped on entry), but an existing record's idNumber comes
  // back formatted WITH dashes (e.g. "784-1980-5453534-1"). Comparing raw
  // strings never matched, so the auto-link never fired (verified on dev
  // 2026-07-27). Strip separators + upper-case so both sides line up, while
  // still distinguishing genuinely different IDs (incl. alphanumeric passports).
  const norm = (s: string) => s.replace(/[-\s]/g, '').toUpperCase();
  const entered = norm(enteredIdNumber);
  if (!entered) return undefined;
  return matches.find(
    (m) =>
      !!m.patientId &&
      norm(m.identification?.idNumber ?? m.idNumber ?? '') === entered,
  );
}

export interface GuardianSectionProps {
  /** Reports the guardian-side matching-records count back to the tab
   *  so the save action can be gated when potential duplicates exist
   *  for the guardian identity. Same shape as `IdentityVerificationSection`. */
  onMatchingCountChange?: (count: number) => void;
}

export function GuardianSection({ onMatchingCountChange }: GuardianSectionProps = {}) {
  const { control, setValue } = useFormContext<PersonalDetailsFormValues>();
  const guardian = useWatch({ control, name: 'guardianIdenitification' });
  const missing = useMissingFields();
  const gendersQ = useGenders();
  const idTypesQ = useIdTypes();

  // Relationship typeahead. Flutter's guardian/patient relation lookup calls
  // `GET /Master/Relation?searchQuery=<q>` with NO category
  // (`FetchRelationshipUseCase(QueryParams(query))` → `searchFor` null →
  // `common_data_source_impl.dart:234-238`). The previous `category: 'guardian'`
  // was a bogus param the backend doesn't expect; dropped so the wire matches
  // Flutter (same as the emergency-contact relation lookup).
  const [relQuery, setRelQuery] = useState('');
  const debouncedRel = useDebounce(relQuery, 300);
  const relationsQ = useRelations(debouncedRel);

  // Only surface the "similar records" match table once the user is actually
  // working on the guardian — either entering a new (unlinked) one or editing an
  // existing one. NOT on plain edit-load of an already-linked guardian, where
  // `/Patients/Validate` returns loose extra matches (e.g. an unrelated record)
  // that self-exclusion can't remove, so the table would pop open on open with no
  // change (reported on dev 2026-07-27). `update()` flips this true on any edit.
  const [guardianTouched, setGuardianTouched] = useState(false);

  // Detect Emirates ID for the guardian's ID number field via the idType GUID,
  // mirroring Flutter (`identity_child_widget.dart:299`). The old name-match
  // (`.includes('emirates')`) was dead — `GET /api/Master/IdentityType` never
  // returns a name containing "emirates" — so the mask + 15-digit cap never
  // applied and raw digits were persisted (PROD-1756). GUID is the truth.
  const isGuardianEmiratesId = isEmiratesIdType(guardian?.idType);

  // Matching-records save-gate for the guardian identity. Flutter
  // (`guardians_identity_verification.dart:75-82`) sends all the
  // guardian's identity fields when looking for duplicates; we mirror.
  // The lookup is enabled only when enough fields are populated to
  // meaningfully match.
  const debouncedGuardianMatching = useDebounce(
    {
      firstName: guardian?.firstName ?? '',
      lastName: guardian?.lastName ?? '',
      birthDate: guardian?.birthDate ?? '',
      gender: guardian?.gender ?? '',
      idType: guardian?.idType ?? '',
      idNumber: (guardian?.idNumber ?? '').trim(),
    },
    500,
  );
  const guardianMatchingQ = useMatchingPatients(
    // Require either (firstName + lastName + birthDate) OR (idType + idNumber)
    // before firing — Flutter does the same gating to avoid noisy lookups
    // on a half-typed form.
    (debouncedGuardianMatching.firstName &&
      debouncedGuardianMatching.lastName &&
      debouncedGuardianMatching.birthDate) ||
      (debouncedGuardianMatching.idType && debouncedGuardianMatching.idNumber)
      ? debouncedGuardianMatching
      : null,
  );
  // Self-exclude the guardian's own record. Flutter does this in
  // `guardians_identity_verification.dart` build():
  //   matchingPatientRecords.removeWhere((e) => e.patientId == guardian.id)
  // Without it, editing a minor whose guardian is already a linked patient
  // flags the guardian as its own "duplicate" and blocks save.
  const guardianMatches = (guardianMatchingQ.data ?? []).filter(
    (m) => !guardian?.id || m.patientId !== guardian.id,
  );

  // Suppression lifecycle — see `useSuppressibleCount` docs + unit test.
  // Same model as IdentityVerificationSection.
  const {
    effectiveCount: effectiveGuardianCount,
    suppress,
  } = useSuppressibleCount(guardianMatches);

  // Show the match surface only when the user is entering a new guardian
  // (`!guardian?.id`) or has edited the existing one (`guardianTouched`) — never
  // on a plain edit-load of an already-linked guardian.
  const showGuardianMatches =
    (guardianTouched || !guardian?.id) &&
    effectiveGuardianCount > 0 &&
    !!(guardian?.idNumber ?? '').trim();

  const idTypeName = (id: string | null) =>
    (id && idTypesQ.data?.find((t) => t.id === id)?.name) || '—';

  // Link an existing person as the guardian — Flutter `onGuardianSelected`
  // (`identity_child_widget.dart:561-591`). Copies the existing record's
  // identity into the guardian and stamps `id` = patientId; the next
  // matching fetch then self-excludes it (above), which clears the table
  // and re-enables save — same net effect as Flutter's `matchingRecords
  // .clear()`. `suppress()` clears the gate immediately in the meantime.
  //
  // Identity comes from the nested `identification` block (top-level
  // idType/idNumber on the Validate row are legacy nulls). Gender uses the
  // GUID `genderId` — NOT the display string `m.gender` ("Male"), which
  // Flutter copies verbatim but which wouldn't match a gender pill or save
  // as a valid GUID.
  const useExistingGuardian = (m: MatchingPatient) => {
    update({
      id: m.patientId ?? null,
      idType: m.identification?.idType ?? m.idType ?? null,
      firstName: m.firstName ?? null,
      middleName: m.middleName ?? null,
      lastName: m.lastName ?? null,
      birthDate: m.birthDate ?? null,
      gender: m.genderId ?? null,
      idNumber: m.identification?.idNumber ?? m.idNumber ?? null,
      idExpirationDate:
        m.identification?.idExpirationDate ?? m.idExpirationDate ?? null,
    });
    suppress();
  };

  // NOTE: we deliberately do NOT auto-link an exact ID-number match. An earlier
  // build (Dr. Johnny's clinic, 2026-07-27) auto-called `useExistingGuardian`
  // from an effect the moment an exact match arrived, to make siblings share one
  // guardian. But that bound the matched person's full identity into the form
  // silently — and because linking stamps `id`, the self-exclude above then
  // dropped the match so the `GuardianMatchTable` never even appeared. The user
  // saw "all the data filled in without me selecting anything" (reported
  // 2026-07-28). Sibling-sharing is preserved WITHOUT the silent bind: the exact
  // match now stays in `guardianMatches` and surfaces in the match table, where
  // "Use Selected Record" calls the same `useExistingGuardian`. The user links
  // it explicitly. Guardian matches don't block Save, so nothing is gated on it.
  // `findExactGuardianIdMatch` is retained (exported + unit-tested) for the
  // table to optionally highlight the exact row in a follow-up.

  useEffect(() => {
    onMatchingCountChange?.(effectiveGuardianCount);
  }, [effectiveGuardianCount, onMatchingCountChange]);

  const update = (
    patch: Partial<NonNullable<PersonalDetailsFormValues['guardianIdenitification']>>,
  ) => {
    // Any edit to the guardian counts as "touched" — enables the match surface
    // (which stays hidden on a plain edit-load of an already-linked guardian).
    setGuardianTouched(true);
    // Guardian wire shape (verified via PUT/POST curls 2026-05-14):
    //   { id, firstName, middleName, lastName, birthDate, gender,
    //     idType, idNumber, idExpirationDate,
    //     relationShip (camelCase capital S, IdNameModel),
    //     relationId (GUID) }
    // PROD-777 originally shipped with lowercase `relationship` plus
    // phantom `email` / `contactNumber` fields that don't exist on the
    // wire — those were silently dropped on save. Fixed here.
    const merged = {
      id: guardian?.id ?? null,
      firstName: guardian?.firstName ?? null,
      middleName: guardian?.middleName ?? null,
      lastName: guardian?.lastName ?? null,
      birthDate: guardian?.birthDate ?? null,
      gender: guardian?.gender ?? null,
      idType: guardian?.idType ?? null,
      idNumber: guardian?.idNumber ?? null,
      idExpirationDate: guardian?.idExpirationDate ?? null,
      relationShip: guardian?.relationShip ?? null,
      relationId: guardian?.relationId ?? null,
      ...patch,
    };
    const allEmpty =
      !merged.firstName &&
      !merged.lastName &&
      !merged.birthDate &&
      !merged.gender &&
      !merged.idType &&
      !merged.idNumber &&
      !merged.relationShip;
    setValue('guardianIdenitification', allEmpty ? null : merged, {
      shouldDirty: true,
    });
  };

  const fromInput = (yyyymmdd: string) => (yyyymmdd ? `${yyyymmdd}T00:00:00.000` : null);
  const toInput = (iso: string | null | undefined) =>
    iso ? iso.split('T')[0] ?? '' : '';

  return (
    <div className="grid gap-6">
      <p className="text-sm text-text-muted">
        Guardian details are required for minor patients (under 18). The
        guardian is the legal contact for clinical decisions, billing, and
        communications.
      </p>

      {/* Names */}
      {/* Every guardian control full-width, one per row — same treatment as the
          patient's own sections, not the old cramped 3-column grids. Relationship
          comes FIRST: it defines the guardian's role before their identity + ID.
          "Do that to all the guardian controls, relation the first field"
          (Andrew 2026-07-28). */}
      <div className="grid gap-6">
        <div>
          <FieldLabel required requiredTone={requiredTone(guardian?.relationShip?.id || guardian?.relationId)}>Relationship to patient</FieldLabel>
          <MasterTypeahead
            value={guardian?.relationShip ?? null}
            onSelect={(item) =>
              update({
                relationShip: item,
                // Mirror the GUID into `relationId` so the wire body has
                // both — Flutter ships them together.
                relationId: item?.id ?? null,
              })
            }
            invalid={missing.has('guardianRelation')}
            placeholder="Search relationship"
            query={relQuery}
            onQueryChange={setRelQuery}
            results={relationsQ.data ?? []}
            isPending={relationsQ.isFetching}
          />
        </div>
        <div>
          <FieldLabel required requiredTone={requiredTone(guardian?.firstName)}>Guardian first name</FieldLabel>
          <TextField
            value={guardian?.firstName ?? ''}
            onChange={(e) => update({ firstName: e.target.value || null })}
            placeholder="Enter first name"
            status={missing.has('guardianFirstName') ? 'error' : undefined}
            className="mt-1"
          />
        </div>
        <div>
          <FieldLabel >
            Middle name
          </FieldLabel>
          <TextField
            value={guardian?.middleName ?? ''}
            onChange={(e) => update({ middleName: e.target.value || null })}
            placeholder="Enter middle name (Optional)"
            className="mt-1"
          />
        </div>
        <div>
          <FieldLabel required requiredTone={requiredTone(guardian?.lastName)}>Last name</FieldLabel>
          <TextField
            value={guardian?.lastName ?? ''}
            onChange={(e) => update({ lastName: e.target.value || null })}
            placeholder="Enter last name"
            status={missing.has('guardianLastName') ? 'error' : undefined}
            className="mt-1"
          />
        </div>
        <div>
          <FieldLabel >
            Date of birth
          </FieldLabel>
          <DatePickerField
            value={toInput(guardian?.birthDate)}
            onChange={(v) => update({ birthDate: fromInput(v) })}
            max={adultMaxDob()}
            className="mt-1"
          />
        </div>
        <div>
          <FieldLabel>Gender</FieldLabel>
          <div className="mt-2">
            {gendersQ.isPending ? (
              <BrandSpinner size={20} label="Loading…" />
            ) : !gendersQ.data || gendersQ.data.length === 0 ? (
              <span className="text-sm text-text-muted">No genders</span>
            ) : (
              <PillPicker
                options={gendersQ.data.map((g) => ({ value: g.id, label: g.name }))}
                value={guardian?.gender ?? null}
                onChange={(v) => update({ gender: v })}
              />
            )}
          </div>
        </div>
        <div>
          <FieldLabel>ID type</FieldLabel>
          {/* Pills — same control the patient's own Identity Verification uses
              (`identity-verification-section.tsx`), not a dropdown. Keeps the
              guardian ID block visually identical to the patient EID block
              (Andrew 2026-07-28). The old base-ui Select surfaced the raw idType
              GUID in the trigger before the async options mounted. */}
          <div className="mt-2">
            {idTypesQ.isPending ? (
              <BrandSpinner size={20} label="Loading…" />
            ) : (
              <PillPicker
                options={(idTypesQ.data ?? []).map((t) => ({
                  value: t.id,
                  label: t.name,
                }))}
                value={guardian?.idType ?? null}
                onChange={(v) => update({ idType: v || null })}
              />
            )}
          </div>
        </div>
        <div>
          <FieldLabel>ID number</FieldLabel>
          {isGuardianEmiratesId ? (
            <TextField
              value={guardian?.idNumber ? formatEmiratesId(guardian.idNumber) : ''}
              onChange={(e) =>
                update({
                  idNumber: e.target.value.replace(/\D+/g, '').slice(0, 15) || null,
                })
              }
              placeholder="784-YYYY-XXXXXXX-X"
              className="mt-1"
              inputMode="numeric"
              status={missing.has('guardianIdNumber') ? 'error' : undefined}
            />
          ) : (
            <TextField
              value={guardian?.idNumber ?? ''}
              onChange={(e) => update({ idNumber: e.target.value || null })}
              placeholder="Enter ID number"
              className="mt-1"
              status={missing.has('guardianIdNumber') ? 'error' : undefined}
            />
          )}
          {/* Inline ID-number duplicate warning — Flutter
              `identity_child_widget.dart:324` shows this on the field when
              matches exist and the ID number is filled. Copy verbatim from
              `messages_en.dart:thisIdNumberAlreadyExists…`. Gated on the
              effective (post-suppress) count so "Create New Record Anyway"
              clears it, matching Flutter's UseRecordAnyway. */}
          {showGuardianMatches ? (
            <p className="mt-1 text-xs text-text-muted">
              This guardian may already be in the system. Pick a matching
              record below to link them — or just continue. This won't block
              saving.
            </p>
          ) : null}
        </div>
        <div>
          <FieldLabel>ID expiration date</FieldLabel>
          {/* Flutter parity — the guardian identity has an ID expiry field
              (`identity_child_widget.dart`). Without it the user can't satisfy
              the "complete the ID details — number, type, and expiry" save-gate.
              Persisted on guardianIdenitification.idExpirationDate. */}
          <DatePickerField
            value={toInput(guardian?.idExpirationDate)}
            onChange={(v) => update({ idExpirationDate: fromInput(v) })}
            // PROD-1762: same bound as the patient ID-expiry field. Both come
            // from one Flutter widget branching on `isGuardian`, so fixing only
            // the patient side would leave a live inconsistency here.
            min={idExpiryMin()}
            className="mt-1"
          />
        </div>
      </div>

      {/* Matching-records — inline list, Flutter parity (PROD-726).
          Replaces the earlier banner+modal: Flutter renders the matches
          inline via PatientSelectionWidget and selecting one LINKS the
          existing person as the guardian (auto-fill), it does not navigate
          away. Only shown once an ID number is entered, mirroring
          `identity_child_widget.dart:492` (`!isBlank(...idNumber)`). */}
      {showGuardianMatches ? (
        <GuardianMatchTable
          matches={guardianMatches}
          idTypeName={idTypeName}
          onUseRecord={useExistingGuardian}
          onCreateNewAnyway={suppress}
        />
      ) : null}

      {/* Email + Contact fields removed — the wire shape (Flutter
          `GuardianIdenitification` model + verified curls 2026-05-14)
          has no `email` or `contactNumber` for the guardian. PROD-777
          shipped these as phantom fields whose values were silently
          dropped on save. Removed rather than rebound so the UI stops
          implying the data persists. */}
    </div>
  );
}
